import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { AuthTokens } from '@/types'
import { isNativePlatform } from '@/lib/native'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

// LIFF-H3: project token convention — access token in MEMORY, refresh token in
// localStorage. The legacy localStorage access-token key is cleared on logout.
const REFRESH_TOKEN_KEY = 'liff_refresh_token'
const LEGACY_ACCESS_TOKEN_KEY = 'liff_access_token'

let accessToken: string | null = null

export const getAccessToken = () => accessToken

export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

export const hasRefreshToken = () => !!getRefreshToken()

export const setTokens = (tokens: AuthTokens) => {
  accessToken = tokens.access_token
  if (tokens.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
  }
}

export const clearTokens = () => {
  accessToken = null
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(LEGACY_ACCESS_TOKEN_KEY)
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add access token and say which client is calling.
// X-FlowRent-Client: 'app' inside the Capacitor shell (the generic native app
// AND every white-label app/<client> build alike, per EP-152 decision 2, no
// per-client slug), 'liff' otherwise (LINE's in-app browser or a bare web
// preview, the two can't be told apart client-side, and only the former is
// real production traffic). Read by RecordsCustomerDailyActivity on the API
// side; the two pre-auth calls below (verifyOtp, resolveLiffAccount) use the
// bare axios import and never go through this interceptor.
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    if (config.headers) {
      config.headers['X-FlowRent-Client'] = isNativePlatform() ? 'app' : 'liff'
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Single-flight refresh: concurrent 401s (e.g. the 5s payment-status poll plus
// a page query) share ONE refresh call — the server rotates the refresh token
// on use, so racing refreshes would kill the session mid-payment.
let refreshPromise: Promise<AuthTokens> | null = null

export const refreshTokens = async (): Promise<AuthTokens> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const refreshToken = getRefreshToken()
      if (!refreshToken) throw new Error('no refresh token')
      const response = await axios.post<AuthTokens>(
        `${API_BASE_URL}/api/customers/refresh_token`,
        { refresh_token: refreshToken }
      )
      setTokens(response.data)
      return response.data
    })().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

// Debounced login redirect — N failing requests must not stack N redirects.
let redirecting = false
const redirectToLogin = () => {
  clearTokens()
  if (!redirecting) {
    redirecting = true
    window.location.href = '/login'
  }
}

// Response interceptor — on 401, refresh once (single-flight) and retry the
// original request; only a failed refresh logs the customer out.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry && hasRefreshToken()) {
      originalRequest._retry = true
      try {
        const tokens = await refreshTokens()
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
        }
        return api(originalRequest)
      } catch {
        redirectToLogin()
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401) {
      redirectToLogin()
    }
    return Promise.reject(error)
  }
)

// Customer auth: send OTP
export const sendOtp = async (leaseCode: string): Promise<{ message: string; phone_hint: string }> => {
  const response = await api.post('/api/customers/send_auth_code', { lease_code: leaseCode })
  return response.data
}

// Customer auth: verify OTP and get token.
// Token is minted server-side by /api/customers/verify_auth_code — no OAuth
// client credentials in the bundle (LIFF-C1).
export const verifyOtp = async (
  leaseCode: string,
  authCode: string
): Promise<AuthTokens> => {
  const response = await axios.post<AuthTokens>(`${API_BASE_URL}/api/customers/verify_auth_code`, {
    lease_code: leaseCode,
    auth_code: authCode,
  })
  return response.data
}

// Native customer app (app/): post-login account resolution from the session.
// Same payload shape as resolveLiffAccount — the server builds both through
// one concern (LiffAccountPayload) so they cannot drift. Uses the authed
// `api` instance, so the token-refresh interceptor applies.
export const fetchSessionAccount = async () => {
  const response = await api.get('/api/customer/account')
  return response.data
}

// Resolve account from LIFF ID
export const resolveLiffAccount = async (liffId: string) => {
  const response = await axios.get(`${API_BASE_URL}/api/liff/resolve`, {
    params: { liff_id: liffId },
  })
  return response.data
}

export default api
